import { useRef, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { 
  Users, Baby, Clock, MapPin, User, 
  ChevronLeft, ChevronRight, X
} from 'lucide-react';

const QuickFilterChip = ({ label, icon: Icon, active, onClick, color = 'slate' }) => {
  const colorClasses = {
    slate: active 
      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white' 
      : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600',
    blue: active
      ? 'bg-blue-500 text-white border-blue-500'
      : 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-950/50',
    pink: active
      ? 'bg-pink-500 text-white border-pink-500'
      : 'bg-pink-50 dark:bg-pink-950/30 text-pink-700 dark:text-pink-300 border-pink-200 dark:border-pink-800 hover:bg-pink-100 dark:hover:bg-pink-950/50',
    amber: active
      ? 'bg-amber-500 text-white border-amber-500'
      : 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-950/50',
    red: active
      ? 'bg-red-500 text-white border-red-500'
      : 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-950/50',
    green: active
      ? 'bg-green-500 text-white border-green-500'
      : 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-950/50',
    purple: active
      ? 'bg-purple-500 text-white border-purple-500'
      : 'bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-950/50',
  };

  return (
    <button
      onClick={onClick}
      className={`
        inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
        border transition-all whitespace-nowrap flex-shrink-0
        ${colorClasses[color]}
      `}
      data-testid={`quick-filter-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {label}
      {active && <X className="w-3 h-3 ml-0.5" />}
    </button>
  );
};

export const QuickFilterBar = ({
  genderFilter,
  setGenderFilter,
  ageFilter,
  setAgeFilter,
  yearMissingFilter,
  setYearMissingFilter,
  onClearAll
}) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Check scroll position to show/hide arrows
  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeftArrow(scrollLeft > 10);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 150;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  // Count active quick filters
  const activeCount = [
    genderFilter !== 'all',
    ageFilter !== 'all',
    yearMissingFilter !== 'all'
  ].filter(Boolean).length;

  // Toggle filter helper
  const toggle = (current, value, setter) => {
    setter(current === value ? 'all' : value);
  };

  return (
    <div className="relative">
      {/* Left scroll arrow */}
      {showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-gradient-to-r from-white via-white dark:from-slate-900 dark:via-slate-900 to-transparent flex items-center justify-start"
        >
          <ChevronLeft className="w-4 h-4 text-slate-500" />
        </button>
      )}

      {/* Scrollable filter chips */}
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1 px-1 -mx-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {/* Clear all - only show when filters are active */}
        {activeCount > 0 && (
          <button
            onClick={onClearAll}
            className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-red-500 hover:text-red-600 font-medium whitespace-nowrap flex-shrink-0"
          >
            <X className="w-3.5 h-3.5" />
            Clear
          </button>
        )}

        {/* Gender filters */}
        <QuickFilterChip
          label="Male"
          icon={User}
          color="blue"
          active={genderFilter === 'male'}
          onClick={() => toggle(genderFilter, 'male', setGenderFilter)}
        />
        <QuickFilterChip
          label="Female"
          icon={User}
          color="pink"
          active={genderFilter === 'female'}
          onClick={() => toggle(genderFilter, 'female', setGenderFilter)}
        />

        {/* Divider */}
        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 flex-shrink-0" />

        {/* Age filters */}
        <QuickFilterChip
          label="Children"
          icon={Baby}
          color="amber"
          active={ageFilter === 'child'}
          onClick={() => toggle(ageFilter, 'child', setAgeFilter)}
        />
        <QuickFilterChip
          label="Teens"
          color="amber"
          active={ageFilter === 'teen'}
          onClick={() => toggle(ageFilter, 'teen', setAgeFilter)}
        />
        <QuickFilterChip
          label="Adults"
          color="slate"
          active={ageFilter === 'adult'}
          onClick={() => toggle(ageFilter, 'adult', setAgeFilter)}
        />

        {/* Divider */}
        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 flex-shrink-0" />

        {/* Time filters */}
        <QuickFilterChip
          label="Recent"
          icon={Clock}
          color="red"
          active={yearMissingFilter === 'last_year'}
          onClick={() => toggle(yearMissingFilter, 'last_year', setYearMissingFilter)}
        />
        <QuickFilterChip
          label="1-5 Years"
          color="slate"
          active={yearMissingFilter === '1_5_years'}
          onClick={() => toggle(yearMissingFilter, '1_5_years', setYearMissingFilter)}
        />
        <QuickFilterChip
          label="Cold Cases"
          color="purple"
          active={yearMissingFilter === '10_plus_years'}
          onClick={() => toggle(yearMissingFilter, '10_plus_years', setYearMissingFilter)}
        />
      </div>

      {/* Right scroll arrow */}
      {showRightArrow && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-gradient-to-l from-white via-white dark:from-slate-900 dark:via-slate-900 to-transparent flex items-center justify-end"
        >
          <ChevronRight className="w-4 h-4 text-slate-500" />
        </button>
      )}
    </div>
  );
};

export default QuickFilterBar;
